import React from 'react';
import { maskPhoneInput, normalizePhone, PHONE_LABELS } from '../../lib/phone';
import { ChoiceKeys, Button, IconButton } from '../ui';

// ===== PHONES EDITOR (sql/068) =====
// Shared add/remove/label/set-primary editor for clients.phones, used by
// the desktop ClientIntakeModal AND the estimator intake. Value shape:
// [{ number, label, is_primary }] with numbers kept as the user typed
// (masked); callers normalize on save via normalizePhonesForSave().
//
// Design notes:
//   • Label (Mobile / Landline / Work / Other) is a ChoiceKeys row under
//     the number — four static options, every one visible, one tap
//     (form-controls.md short list). Replaced the tap-to-cycle chip.
//   • Primary is a star toggle: exactly one row is primary; tapping a
//     non-primary row's star moves it. Landline-primary is the whole
//     point of this feature (tester feedback 2026-07-02).
//   • Removing the primary row promotes the first remaining row.

export function normalizePhonesForSave(rows) {
    const cleaned = (rows || [])
        .map(r => ({ number: normalizePhone(r.number), label: r.label || 'Mobile', is_primary: !!r.is_primary }))
        .filter(r => r.number);
    if (cleaned.length && !cleaned.some(r => r.is_primary)) cleaned[0].is_primary = true;
    // Exactly one primary.
    let seen = false;
    for (const r of cleaned) {
        if (r.is_primary) {
            if (seen) r.is_primary = false;
            seen = true;
        }
    }
    return cleaned;
}

// `inline` (Jaylen 2026-09-17, condensed Contact block): the type keys sit
// on the SAME row as the number (★ · number · Mobile Landline Work Other · ✕)
// instead of on a row beneath; wraps under the number when the row is too
// narrow (phone). The add link becomes a small secondary button.
export default function PhonesEditor({ value, onChange, inputCls, error, compact = false, inline = false }) {
    const rows = (value && value.length) ? value : [{ number: '', label: 'Mobile', is_primary: true }];

    const update = (idx, patch) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        onChange(next);
    };
    const setPrimary = (idx) => {
        onChange(rows.map((r, i) => ({ ...r, is_primary: i === idx })));
    };
    const remove = (idx) => {
        const next = rows.filter((_, i) => i !== idx);
        if (next.length && !next.some(r => r.is_primary)) next[0] = { ...next[0], is_primary: true };
        onChange(next.length ? next : [{ number: '', label: 'Mobile', is_primary: true }]);
    };
    const add = () => onChange([...rows, { number: '', label: 'Mobile', is_primary: rows.length === 0 }]);
    const labelItems = PHONE_LABELS.map(l => ({ value: l, label: l }));

    const keys = (idx, r) => (
        <ChoiceKeys
            size="sm"
            className={inline ? 'flex-shrink-0' : 'ml-[42px]'}
            ariaLabel={`Phone ${idx + 1} type`}
            items={labelItems}
            value={r.label || 'Mobile'}
            onChange={v => update(idx, { label: v })}
        />
    );

    return (
        <div className="space-y-2">
            {rows.map((r, idx) => (
                <div key={idx} className="space-y-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Primary star — what the system calls/texts/displays */}
                        <button
                            type="button"
                            onClick={() => setPrimary(idx)}
                            title={r.is_primary ? 'Primary number' : 'Make this the primary number'}
                            aria-label={r.is_primary ? 'Primary number' : 'Make primary'}
                            aria-pressed={r.is_primary}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-[16px] transition-colors ${r.is_primary ? 'bg-amber-100 text-amber-500 border border-amber-300' : 'bg-slate-50 text-slate-300 border border-slate-200 hover:text-amber-400'}`}
                        >
                            ★
                        </button>
                        <input
                            type="tel"
                            inputMode="tel"
                            autoComplete={idx === 0 ? 'tel' : 'off'}
                            className={`${inputCls} flex-1 ${inline ? 'min-w-[180px]' : 'min-w-0'} ${idx === 0 && error ? 'border-red-400 ring-1 ring-red-400' : ''}`}
                            value={r.number}
                            onChange={e => update(idx, { number: maskPhoneInput(e.target.value) })}
                            placeholder="(828) 555-0123"
                        />
                        {inline && keys(idx, r)}
                        {rows.length > 1 && (
                            <IconButton
                                label="Remove this number"
                                tone="danger"
                                onClick={() => remove(idx)}
                            >
                                ✕
                            </IconButton>
                        )}
                    </div>
                    {/* Number type — one key per label, indented past the star */}
                    {!inline && keys(idx, r)}
                </div>
            ))}
            <Button
                variant={inline ? 'secondary' : 'tertiary'}
                size="sm"
                onClick={add}
                className={compact ? '' : 'mt-0.5'}
            >
                + Add another number
            </Button>
        </div>
    );
}
